/**
 * Optimized Video Lightbox System
 * Performance-focused with conditional loading and minimal overhead
 * -- Developed with love and caffeine by LOOT
 */

(function() {
  'use strict';

  // Early exit if no lightbox exists
  const lightbox = document.querySelector('[data-video-lightbox-init]');
  if (!lightbox) return;

  // Get capabilities from lightbox data attributes
  const hasVimeo = lightbox.getAttribute('data-has-vimeo') === 'true';
  const hasSelfHosted = lightbox.getAttribute('data-has-self-hosted') === 'true';

  // Cache DOM elements for performance
  const elements = {
    lightbox,
    closeButtons: document.querySelectorAll('[data-video-lightbox-control="close"]'),
    playButton: lightbox.querySelector('[data-vimeo-control="play"]'),
    pauseButton: lightbox.querySelector('[data-vimeo-control="pause"]'),
    muteButton: lightbox.querySelector('[data-vimeo-control="mute"]'),
    timelineInput: lightbox.querySelector('[data-vimeo-control="timeline"]'),
    fullscreenButton: lightbox.querySelector('[data-vimeo-control="fullscreen"]'),
    durationSpan: lightbox.querySelector('[data-vimeo-duration]'),
    progressBar: lightbox.querySelector('.vimeo-lightbox__timeline-progress'),
    iframe: hasVimeo ? lightbox.querySelector('.vimeo-lightbox__iframe') : null,
    video: hasSelfHosted ? lightbox.querySelector('.vimeo-lightbox__video') : null,
    darkOverlay: lightbox.querySelector('.vimeo-lightbox__dark'),
    loadingSpinner: lightbox.querySelector('.vimeo-lightbox__loading')
  };

  // State management
  let state = {
    currentVideoSource: '',
    currentVideoId: '',
    currentVideoUrl: '',
    player: null,
    isPlayerReady: false,
    isPlaying: false,
    isMuted: false,
    currentTime: 0,
    duration: 0,
    cleanupSelfHostedEvents: null
  };

  // Performance optimized utility functions
  const utils = {
    // Debounced resize handler for better performance
    debounce(func, wait) {
      let timeout;
      return function executedFunction(...args) {
        const later = () => {
          clearTimeout(timeout);
          func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
      };
    },

    // Format time for display
    formatTime(seconds) {
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = Math.floor(seconds % 60);
      return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
    },

    // Show/hide elements efficiently
    show(element) {
      if (element) element.style.display = 'block';
    },

    hide(element) {
      if (element) element.style.display = 'none';
    },

    // Show elements with flex for proper alignment
    showFlex(element) {
      if (element) element.style.display = 'flex';
    }
  };

  // Video source detection
  function getVideoData(trigger) {
    return {
      source: trigger.getAttribute('data-video-source') || 'vimeo',
      id: trigger.getAttribute('data-video-id') || '',
      url: trigger.getAttribute('data-video-url') || ''
    };
  }

  // Video element management
  function showVideoElement() {
    // Always ensure we have the current iframe reference
    if (state.currentVideoSource === 'vimeo') {
      const currentIframe = elements.lightbox.querySelector('.vimeo-lightbox__iframe');
      if (currentIframe) {
        elements.iframe = currentIframe; // Update reference
        utils.show(elements.iframe);
        utils.hide(elements.video);
      }
    } else if (state.currentVideoSource === 'self_hosted' && elements.video) {
      utils.hide(elements.iframe);
      utils.show(elements.video);
    }
  }

  function hideVideoElements() {
    utils.hide(elements.iframe);
    utils.hide(elements.video);
  }

  // Vimeo player setup (only if Vimeo is supported)
  function setupVimeoPlayer() {
    if (!hasVimeo || !state.currentVideoId) return;

    // Check if Vimeo API is loaded
    if (typeof Vimeo === 'undefined') {
      console.warn('Vimeo API not loaded');
      return;
    }

    // Ensure iframe exists - recreate if missing
    let iframe = elements.lightbox.querySelector('.vimeo-lightbox__iframe');
    if (!iframe) {
      console.log('Iframe missing, recreating...');
      iframe = document.createElement('iframe');
      iframe.width = "640";
      iframe.height = "360";
      iframe.frameBorder = "0";
      iframe.allowFullscreen = true;
      iframe.allow = "autoplay; encrypted-media";
      iframe.className = "vimeo-lightbox__iframe";
      iframe.style.display = "none";
      
      // Insert iframe into the player container
      const playerContainer = elements.lightbox.querySelector('.vimeo-lightbox__player');
      const beforeElement = playerContainer.querySelector('.vimeo-lightbox__before');
      if (beforeElement && beforeElement.nextSibling) {
        playerContainer.insertBefore(iframe, beforeElement.nextSibling);
      } else {
        playerContainer.appendChild(iframe);
      }
      
      // Update our reference
      elements.iframe = iframe;
    }

    // Reset iframe and set new source
    elements.iframe.src = '';
    setTimeout(() => {
      elements.iframe.src = `https://player.vimeo.com/video/${state.currentVideoId}?api=1&background=1&autoplay=0&loop=0&muted=0&quality=auto&transparent=0&responsive=1`;
      
      // Create new player
      setTimeout(() => {
        try {
          state.player = new Vimeo.Player(elements.iframe);
          setupVimeoEvents();
        } catch (error) {
          console.error('Failed to create Vimeo player:', error);
        }
      }, 100);
    }, 50);
  }

  // Self-hosted player setup (only if self-hosted is supported)
  function setupSelfHostedPlayer() {
    if (!hasSelfHosted || !elements.video || !state.currentVideoUrl) return;

    // Set up video source
    const source = elements.video.querySelector('source');
    if (source) {
      source.src = state.currentVideoUrl;
    } else {
      // Fallback: set video src directly
      elements.video.src = state.currentVideoUrl;
    }
    
    // Load the video
    elements.video.load();
    setupSelfHostedEvents();
  }

  // Event handlers for Vimeo
  function setupVimeoEvents() {
    if (!state.player) return;

    state.player.ready().then(() => {
      state.isPlayerReady = true;
      elements.lightbox.setAttribute('data-vimeo-loaded', 'true');
      elements.lightbox.setAttribute('data-vimeo-activated', 'true');
      hideLoadingSpinner();
      
      // Ensure iframe is visible when player is ready
      const currentIframe = elements.lightbox.querySelector('.vimeo-lightbox__iframe');
      if (currentIframe) {
        elements.iframe = currentIframe;
        utils.show(elements.iframe);
      }
      
      runSizing();
    }).catch(console.error);

    state.player.on('play', () => {
      state.isPlaying = true;
      elements.lightbox.setAttribute('data-vimeo-playing', 'true');
      updatePlayPauseButtons();
    });

    state.player.on('pause', () => {
      state.isPlaying = false;
      elements.lightbox.setAttribute('data-vimeo-playing', 'false');
      updatePlayPauseButtons();
    });

    state.player.on('timeupdate', (data) => {
      state.currentTime = data.seconds;
      updateTimeline();
    });

    state.player.getDuration().then((dur) => {
      state.duration = dur;
      updateDuration();
    }).catch(console.error);

    state.player.on('volumechange', (data) => {
      state.isMuted = data.muted;
      updateMuteButton();
    });
  }

  // Event handlers for self-hosted video
  function setupSelfHostedEvents() {
    if (!elements.video) return;

    // Clean up any existing events first
    if (state.cleanupSelfHostedEvents) {
      state.cleanupSelfHostedEvents();
    }

    const handleLoadedMetadata = () => {
      state.isPlayerReady = true;
      state.duration = elements.video.duration;
      elements.lightbox.setAttribute('data-vimeo-loaded', 'true');
      elements.lightbox.setAttribute('data-vimeo-activated', 'true');
      hideLoadingSpinner();
      updateDuration();
      runSizing();
    };

    const handlePlay = () => {
      state.isPlaying = true;
      elements.lightbox.setAttribute('data-vimeo-playing', 'true');
      updatePlayPauseButtons();
    };

    const handlePause = () => {
      state.isPlaying = false;
      elements.lightbox.setAttribute('data-vimeo-playing', 'false');
      updatePlayPauseButtons();
    };

    const handleTimeUpdate = () => {
      state.currentTime = elements.video.currentTime;
      updateTimeline();
    };

    const handleVolumeChange = () => {
      state.isMuted = elements.video.muted;
      updateMuteButton();
    };

    // Add event listeners
    elements.video.addEventListener('loadedmetadata', handleLoadedMetadata);
    elements.video.addEventListener('play', handlePlay);
    elements.video.addEventListener('pause', handlePause);
    elements.video.addEventListener('timeupdate', handleTimeUpdate);
    elements.video.addEventListener('volumechange', handleVolumeChange);

    // Store cleanup function
    state.cleanupSelfHostedEvents = () => {
      if (elements.video) {
        elements.video.removeEventListener('loadedmetadata', handleLoadedMetadata);
        elements.video.removeEventListener('play', handlePlay);
        elements.video.removeEventListener('pause', handlePause);
        elements.video.removeEventListener('timeupdate', handleTimeUpdate);
        elements.video.removeEventListener('volumechange', handleVolumeChange);
      }
    };
  }

  // UI update functions
  function updatePlayPauseButtons() {
    if (state.isPlaying) {
      utils.hide(elements.playButton);
      utils.showFlex(elements.pauseButton);
    } else {
      utils.showFlex(elements.playButton);
      utils.hide(elements.pauseButton);
      
      // When paused, keep showing current time (don't revert to total duration)
      if (elements.durationSpan && state.currentTime > 0) {
        elements.durationSpan.textContent = utils.formatTime(state.currentTime);
      }
    }
  }

  function updateMuteButton() {
    if (!elements.muteButton) return;
    
    const volumeUp = elements.muteButton.querySelector('.vimeo-lightbox__volume-up-svg');
    const volumeMute = elements.muteButton.querySelector('.vimeo-lightbox__volume-mute-svg');
    
    if (state.isMuted) {
      utils.hide(volumeUp);
      utils.show(volumeMute);
    } else {
      utils.show(volumeUp);
      utils.hide(volumeMute);
    }
  }

  function updateTimeline() {
    if (state.duration > 0 && elements.timelineInput && elements.progressBar) {
      const progress = (state.currentTime / state.duration) * 100;
      elements.timelineInput.value = progress;
      elements.progressBar.value = progress;
    }
    
    // Update time display to show current time during playback
    if (elements.durationSpan && state.isPlaying) {
      elements.durationSpan.textContent = utils.formatTime(state.currentTime);
    }
  }

  function updateDuration() {
    // Show total duration when video loads (initial state)
    if (state.duration > 0 && elements.durationSpan) {
      elements.durationSpan.textContent = utils.formatTime(state.duration);
    }
  }

  function hideLoadingSpinner() {
    if (elements.loadingSpinner) {
      elements.loadingSpinner.style.opacity = '0';
    }
  }

  function showLoadingSpinner() {
    if (elements.loadingSpinner) {
      elements.loadingSpinner.style.opacity = '1';
    }
  }

  // Video control functions
  function playVideo() {
    if (state.currentVideoSource === 'vimeo' && state.player) {
      state.player.play().catch(console.error);
    } else if (state.currentVideoSource === 'self_hosted' && elements.video) {
      elements.video.play().catch(console.error);
    }
  }

  function pauseVideo() {
    if (state.currentVideoSource === 'vimeo' && state.player) {
      state.player.pause().catch(console.error);
    } else if (state.currentVideoSource === 'self_hosted' && elements.video) {
      elements.video.pause();
    }
  }

  function toggleMute() {
    if (state.currentVideoSource === 'vimeo' && state.player) {
      state.player.setMuted(!state.isMuted).catch(console.error);
    } else if (state.currentVideoSource === 'self_hosted' && elements.video) {
      elements.video.muted = !state.isMuted;
    }
  }

  function seekTo(progress) {
    const time = (progress / 100) * state.duration;
    if (state.currentVideoSource === 'vimeo' && state.player) {
      state.player.setCurrentTime(time).catch(console.error);
    } else if (state.currentVideoSource === 'self_hosted' && elements.video) {
      elements.video.currentTime = time;
    }
  }

  function toggleFullscreen() {
    if (state.currentVideoSource === 'vimeo' && state.player) {
      state.player.requestFullscreen().catch(console.error);
    } else if (state.currentVideoSource === 'self_hosted' && elements.video && elements.video.requestFullscreen) {
      elements.video.requestFullscreen().catch(console.error);
    }
  }

  // Original sizing function - EXACT copy from working video-lightbox.js
  async function runSizing() {
    if (state.currentVideoSource === 'vimeo' && (!state.player || !state.isPlayerReady)) return;
    if (state.currentVideoSource === 'self_hosted' && (!elements.video || !state.isPlayerReady)) return;

    try {
      const mode = elements.lightbox.getAttribute('data-vimeo-update-size');
      let ar = 0.5625; // Default 16:9 aspect ratio

      // Get aspect ratio based on video source
      if (state.currentVideoSource === 'vimeo' && state.player) {
        const w = await state.player.getVideoWidth();
        const h = await state.player.getVideoHeight();
        ar = h / w;
      } else if (state.currentVideoSource === 'self_hosted' && elements.video) {
        ar = elements.video.videoHeight / elements.video.videoWidth || 0.5625;
      }

      console.log('Running sizing - aspect ratio:', ar, 'mode:', mode);

      const beforeEl = elements.lightbox.querySelector('.vimeo-lightbox__before');
      
      if (mode === 'true') {
        if (beforeEl) {
          const newPaddingTop = (ar * 100) + '%';
          const currentPaddingTop = beforeEl.style.paddingTop;

          // Only update padding-top if significantly different to minimize jolting
          if (!currentPaddingTop || Math.abs(parseFloat(currentPaddingTop) - (ar * 100)) > 2) {
            beforeEl.style.paddingTop = newPaddingTop;
            console.log('Updated padding-top from', currentPaddingTop, 'to', newPaddingTop);
          }
        }
        clampWrapSize(ar);
      } else {
        clampWrapSize(ar);
      }

      // Ensure video elements fill container properly - ORIGINAL approach
      const activeVideoElement = state.currentVideoSource === 'vimeo' ? elements.iframe : elements.video;
      if (activeVideoElement) {
        activeVideoElement.style.width = '100%';
        activeVideoElement.style.height = '100%';
        activeVideoElement.style.border = 'none';
        activeVideoElement.style.outline = 'none';
      }
    } catch (error) {
      console.log('Sizing error:', error);
      // Fallback sizing if video dimensions fail - ORIGINAL approach
      const activeVideoElement = state.currentVideoSource === 'vimeo' ? elements.iframe : elements.video;
      if (activeVideoElement) {
        activeVideoElement.style.width = '100%';
        activeVideoElement.style.height = '100%';
        activeVideoElement.style.border = 'none';
        activeVideoElement.style.outline = 'none';
      }
    }
  }

  // Original clampWrapSize function - EXACT copy from working video-lightbox.js
  function clampWrapSize(ar) {
    const calcEl = elements.lightbox.querySelector('.vimeo-lightbox__calc');
    const wrapEl = elements.lightbox.querySelector('.vimeo-lightbox__calc-wrap');
    if (!calcEl || !wrapEl) return;

    const w = calcEl.offsetWidth;
    const h = calcEl.offsetHeight;
    wrapEl.style.maxWidth = Math.min(w, h / ar) + 'px';
  }

  // Open lightbox function
  function openLightbox(trigger) {
    const videoData = getVideoData(trigger);
    
    // Clean up any existing player first
    if (state.player && state.currentVideoSource === 'vimeo') {
      state.player.destroy?.().catch(console.error);
      state.player = null;
    }
    
    // Clean up self-hosted events
    if (state.cleanupSelfHostedEvents) {
      state.cleanupSelfHostedEvents();
      state.cleanupSelfHostedEvents = null;
    }
    
    // Reset state
    Object.assign(state, {
      currentVideoSource: videoData.source,
      currentVideoId: videoData.id,
      currentVideoUrl: videoData.url,
      isPlayerReady: false,
      isPlaying: false,
      currentTime: 0,
      duration: 0
    });

    // Update lightbox attributes
    elements.lightbox.setAttribute('data-video-source', state.currentVideoSource);
    elements.lightbox.setAttribute('data-video-id', state.currentVideoId);
    elements.lightbox.setAttribute('data-vimeo-activated', 'loading');
    elements.lightbox.setAttribute('data-vimeo-playing', 'false');
    elements.lightbox.setAttribute('data-vimeo-loaded', 'false');

    // Reset UI to initial state
    resetUIElements();
    showLoadingSpinner();
    showVideoElement();

    // Initialize player based on source and capability
    if (state.currentVideoSource === 'vimeo' && hasVimeo) {
      setupVimeoPlayer();
    } else if (state.currentVideoSource === 'self_hosted' && hasSelfHosted) {
      setupSelfHostedPlayer();
    }

    // Fallback sizing
    setTimeout(() => {
      if (state.isPlayerReady) runSizing();
    }, 500);
  }

  // Close lightbox function
  function closeLightbox() {
    elements.lightbox.setAttribute('data-vimeo-activated', 'false');
    
    // Pause video if playing
    if (state.isPlaying) {
      pauseVideo();
    }
    
    // Hide video elements
    hideVideoElements();

    // Reset state for next open
    Object.assign(state, {
      isPlayerReady: false,
      isPlaying: false,
      currentTime: 0,
      duration: 0
    });

    // Clean up Vimeo player completely
    if (state.player && state.currentVideoSource === 'vimeo') {
      state.player.destroy?.().catch(console.error);
      state.player = null;
    }

    // Reset self-hosted video
    if (elements.video && state.currentVideoSource === 'self_hosted') {
      elements.video.pause();
      elements.video.currentTime = 0;
    }

    // Reset UI elements to initial state
    resetUIElements();
  }

  // Reset video elements to initial state
  function resetVideoElements() {
    // Hide both video elements
    hideVideoElements();
    
    // Reset self-hosted video (but don't clear src yet)
    if (elements.video) {
      elements.video.pause();
      elements.video.currentTime = 0;
    }
    
    // Don't reset Vimeo iframe src here - let it be handled in setup
  }

  // Reset UI elements to initial state
  function resetUIElements() {
    // Reset play/pause buttons
    utils.showFlex(elements.playButton);
    utils.hide(elements.pauseButton);
    
    // Reset timeline
    if (elements.timelineInput) elements.timelineInput.value = 0;
    if (elements.progressBar) elements.progressBar.value = 0;
    
    // Reset duration
    if (elements.durationSpan) elements.durationSpan.textContent = '00:00';
    
    // Reset mute button
    if (elements.muteButton) {
      const volumeUp = elements.muteButton.querySelector('.vimeo-lightbox__volume-up-svg');
      const volumeMute = elements.muteButton.querySelector('.vimeo-lightbox__volume-mute-svg');
      utils.show(volumeUp);
      utils.hide(volumeMute);
    }
    
    // Reset lightbox attributes
    elements.lightbox.setAttribute('data-vimeo-playing', 'false');
    elements.lightbox.setAttribute('data-vimeo-loaded', 'false');
    elements.lightbox.setAttribute('data-vimeo-muted', 'false');
  }

  // Event listeners setup
  function setupEventListeners() {
    // Close buttons
    elements.closeButtons.forEach(btn => {
      btn.addEventListener('click', closeLightbox);
    });

    // Video controls
    if (elements.playButton) {
      elements.playButton.addEventListener('click', playVideo);
    }

    if (elements.pauseButton) {
      elements.pauseButton.addEventListener('click', pauseVideo);
    }

    if (elements.muteButton) {
      elements.muteButton.addEventListener('click', toggleMute);
    }

    if (elements.timelineInput) {
      elements.timelineInput.addEventListener('input', (e) => {
        seekTo(parseFloat(e.target.value));
      });
    }

    if (elements.fullscreenButton) {
      elements.fullscreenButton.addEventListener('click', toggleFullscreen);
    }

    // Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && elements.lightbox.getAttribute('data-vimeo-activated') !== 'false') {
        closeLightbox();
      }
    });

    // Resize handler (same as original)
    window.addEventListener('resize', () => {
      if (state.isPlayerReady) runSizing();
    });

    // Hover functionality for interface controls
    const playerContainer = elements.lightbox.querySelector('.vimeo-lightbox__player');
    if (playerContainer) {
      playerContainer.addEventListener('mouseenter', () => {
        elements.lightbox.setAttribute('data-vimeo-hover', 'true');
      });

      playerContainer.addEventListener('mouseleave', () => {
        elements.lightbox.setAttribute('data-vimeo-hover', 'false');
      });
    }
  }

  // Initialize triggers
  function setupTriggers() {
    // Use event delegation for better performance with dynamic content
    document.addEventListener('click', (e) => {
      const trigger = e.target.closest('[data-video-lightbox-control="open"]');
      if (trigger) {
        e.preventDefault();
        openLightbox(trigger);
      }
    });
  }

  // Initialize everything
  function init() {
    setupEventListeners();
    setupTriggers();
  }

  // Cleanup function
  function cleanup() {
    if (state.player) {
      state.player.destroy?.().catch(console.error);
    }
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Store cleanup function globally
  window.optimizedVideoLightboxCleanup = cleanup;

})();
